import { get, post, put, patch, del, download } from '@/services/http';

// Canonical status type for Purchase Orders
export type PurchaseOrderStatus = 'draft' | 'pending_approval' | 'confirmed' | 'partial_received' | 'received' | 'completed' | 'canceled';

// Per-merchant SendGrid rate-limit error returned by the backend on PO send
// (see assisty-bot AS-9905). The body shape is:
//   { message: string, data: { rateLimit: { suspended: boolean, exhaustedWindow: string|null } } }
// We expose the parsed fields as a typed Error so callers can render a
// friendly toast instead of a generic "Failed to send" message.
export interface IPoEmailRateLimitInfo {
  suspended: boolean;
  exhaustedWindow: string | null;
}

export class PoEmailRateLimitError extends Error {
  public readonly suspended: boolean;
  public readonly exhaustedWindow: string | null;
  public readonly serverMessage: string;

  constructor(serverMessage: string, info: IPoEmailRateLimitInfo) {
    super(serverMessage);
    // Object.setPrototypeOf is required for `instanceof` to work in TS
    // when extending built-in classes that downlevel to ES5.
    Object.setPrototypeOf(this, PoEmailRateLimitError.prototype);
    this.name = 'PoEmailRateLimitError';
    this.suspended = !!info.suspended;
    this.exhaustedWindow = info.exhaustedWindow || null;
    this.serverMessage = serverMessage;
  }
}

/**
 * Returns a user-facing string explaining a PoEmailRateLimitError.
 *
 * Kept as a pure exported function so it can be unit-tested directly
 * without spinning up the parent Vue component. Snackbar strings are plain
 * English to match the existing pattern in PurchaseOrderDetail.vue —
 * promote to i18n when the surrounding messages are.
 */
export function poEmailRateLimitMessage(err: PoEmailRateLimitError): string {
  if (err.suspended) {
    return 'Email sending is temporarily suspended for this store. Please contact support.';
  }
  switch (err.exhaustedWindow) {
    case 'perMinute':
      return 'You’re sending PO emails too quickly. Please wait a moment and try again.';
    case 'perHour':
      return 'You’ve reached the hourly email limit for this store. Please try again later.';
    case 'perDay':
      return 'You’ve reached the daily email limit for this store. Please try again tomorrow.';
    case 'uniqueRecipientsPerDay':
      return 'You’ve reached the daily limit on distinct recipients. Please try again tomorrow.';
    default:
      return 'Email rate limit reached. Please try again in a few minutes.';
  }
}

/**
 * Parse a caught http error into a PoEmailRateLimitError if the response
 * matches the AS-9905 rate-limit shape; otherwise return null so callers
 * can rethrow the original error unchanged.
 *
 * `err.response.data` may be either an object or a raw JSON string. The
 * shared axios instance in `services/http.ts` configures a custom
 * `transformResponse` that returns `data` as-is (no JSON.parse), and the
 * downstream parsing only runs on the success path — so on errors the body
 * arrives as a string. Handle both shapes.
 */
export function parsePoEmailRateLimitError(err: any): PoEmailRateLimitError | null {
  const status = err && err.response && err.response.status;
  if (status !== 429) return null;
  let body = err.response.data;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      return null;
    }
  }
  const rl = body && body.data && body.data.rateLimit;
  if (!rl || typeof rl !== 'object') return null;
  return new PoEmailRateLimitError(
    (body && body.message) || 'Email rate limit reached.',
    { suspended: !!rl.suspended, exhaustedWindow: rl.exhaustedWindow || null }
  );
}

export interface IPurchaseOrderItem {
  id?: number;
  purchaseOrderId?: number;
  variantId: number;
  // The backend has always returned this alongside variantId (it is in the
  // controller's attribute list for every PO read) and the relink dialog has
  // always displayed it; it was simply never declared here. Optional and
  // nullable because a line whose Shopify lookup failed at PO-create time is
  // stored with no inventory item at all — which is exactly the state US-094
  // reports and lets the merchant repair.
  inventoryItemId?: number | string | null;
  variantImage?: string;
  productName: string;
  variant?: string;  // Product variant title (e.g., "Small", "Blue")
  sku: string;
  supplierSku?: string;  // Supplier-specific SKU
  note?: string;
  orderedQuantity: number;
  receivedQuantity: number;
  returnedQuantity?: number;  // Cumulative quantity returned to supplier
  unitPrice: number;
  totalPrice: number;
  _action?: 'add' | 'update' | 'remove';
  extra?: {
    financial?: {
      tax_type_id?: number;
      tax_type_name?: string;
      tax_rate?: number;
      tax_amount?: number;
    };
    landed_cost?: number;
    landed_cost_components?: any;
    unit_price_base?: number;
    tax_amount_base?: number;
    supplier_cost?: number;
    /**
     * US-CURR-013: provenance of `unitPrice`.
     * 'supplier'  = SupplierProduct.cost (authoritative, supplier currency)
     * 'converted' = Shopify store-currency cost / exchange rate (an ESTIMATE — flag for review)
     * 'shopify'   = Shopify cost as-is (same-currency PO, or no rate available)
     */
    cost_source?: 'supplier' | 'converted' | 'shopify';
    [key: string]: any;
  };
}

// IPurchaseReceived and IPurchaseReceivedItem are declared once in
// purchase_received.ts; re-export here so existing `from '@/models/purchase_order'`
// imports keep working without a second (drifting) definition.
// `export type` is required under isolatedModules — runtime `export {...}`
// silently drops type-only entities and produces undefined at consumers.
export type {
  ShopifySyncMetadata,
  IPurchaseReceivedItem,
  IPurchaseReceived,
} from './purchase_received';
import type { IPurchaseReceived } from './purchase_received';
import type { CostSyncAudit, CostCalculationMethod } from '@/views/purchase-order/poCostFinalization';

export interface IPurchaseOrderCostItem {
  id: number;
  totalCost: number;
  unitCost: number;
}

export interface IPurchaseOrderCostData {
  enabled: boolean;
  method: 'none' | 'latest' | 'average';
  updateShopifyCosts: boolean;
  items?: IPurchaseOrderCostItem[];
}

export type PurchaseOrderHistoryType =
  'status_change' | 'email_sent' | 'receive' | 'return' | 'cost_correction' | 'tax_change';

export interface IPurchaseOrderHistoryStatusChangeData {
  previousStatus?: PurchaseOrderStatus;
  newStatus?: PurchaseOrderStatus;
  trigger?: string;
}

export interface IPurchaseOrderHistoryEmailData {
  recipients?: string[];
  ccRecipients?: string[];
  status?: 'sent' | 'failed';
  subject?: string;
}

export interface IPurchaseOrderHistoryReceiveData {
  totalQuantity?: number;
  itemCount?: number;
  receiveId?: number;
}

export interface IPurchaseOrderHistoryReturnData {
  totalQuantity?: number;
  itemCount?: number;
  returnId?: number;
  reason?: string;
}

/** US-093 — the before/after payload written when PO costs are finalised. */
export interface IPurchaseOrderHistoryCostCorrectionData {
  reason?: string | null;
  invoiceNumber?: string | null;
  notes?: string | null;
  shippingBefore?: number;
  shippingAfter?: number;
  adjustmentBefore?: number;
  adjustmentAfter?: number;
  totalAmountBefore?: number;
  totalAmountAfter?: number;
  lines?: Array<{
    poItemId: number;
    sku?: string | null;
    productName?: string | null;
    variantTitle?: string | null;
    previousUnitPrice?: number;
    newUnitPrice?: number;
    previousLandedCost?: number;
    newLandedCost?: number;
  }>;
  costSync?: CostSyncAudit | null;
}

/** AS-10746: the body of PATCH /purchase_orders/:id/items/tax. */
export interface ITaxApplyPayload {
  applyToAll: { taxTypeId: number } | { clear: true };
  reason?: string;
  notes?: string;
}

/** AS-10746: written by PATCH /purchase_orders/:id/items/tax. */
export interface IPurchaseOrderHistoryTaxChangeData {
  lineCount?: number;
  previousGrandTotal?: number;
  newGrandTotal?: number;
  taxTypeName?: string | null;
  cleared?: boolean;
  reason?: string | null;
  notes?: string | null;
}

export type IPurchaseOrderHistoryData =
  | IPurchaseOrderHistoryStatusChangeData
  | IPurchaseOrderHistoryEmailData
  | IPurchaseOrderHistoryReceiveData
  | IPurchaseOrderHistoryReturnData
  | IPurchaseOrderHistoryCostCorrectionData
  | IPurchaseOrderHistoryTaxChangeData;

export interface IPurchaseOrderHistoryItem {
  type: PurchaseOrderHistoryType;
  date: string | null;
  data?: IPurchaseOrderHistoryData;
  performedBy?: string;
  source: 'audit';
}

export interface IAddress {
  name?: string;
  address1?: string;
  address2?: string;
  city?: string;
  province?: string;
  country?: string;
  zip?: string;
  phone?: string;
}

export interface IPurchaseOrderFinancial {
  subtotal?: number;
  item_tax_total?: number;
  shipping_cost?: number;
  shipping_tax_rate?: number;
  shipping_tax_amount?: number;
  total_tax?: number;
  discount_amount?: number;
  discount_type?: 'fixed' | 'percentage';
  discount_calculated?: number;
  adjustment_amount?: number;
  adjustment_note?: string;
  grand_total?: number;
  landed_cost_method?: string;
  landed_cost_ratio?: number;
  avg_landed_cost_per_unit?: number;
  // Multi-currency fields
  exchange_rate?: number | null;
  exchange_rate_source?: string | null;
  exchange_rate_fetched_at?: string | null;
  exchange_rate_manual?: boolean;
  exchange_rate_locked?: boolean;
  exchange_rate_locked_at?: string | null;
  subtotal_base?: number;
  tax_amount_base?: number;
  shipping_cost_base?: number;
  adjustment_amount_base?: number;
  grand_total_base?: number;
  // Payment tracking (Story 19/20) — cached running paid amount
  paid_amount?: number;
  // Invoice date (Story 21) — canonical key under extra.financial
  invoice_date?: string | null;
}

// Story 20: a single payment recorded against a PO (po_payment_records ledger)
export interface IPoPaymentRecord {
  id: number;
  purchaseOrderId: number;
  amount: number;
  paymentDate: string;
  paymentMethod?: string | null;
  referenceNumber?: string | null;
  notes?: string | null;
  createdBy?: string | null;
  createdAt?: string;
}

export interface IPurchaseOrderAttachment {
  filename: string;
  googleDriveFileId: string;
  googleDriveLink: string;
  fileType: string;
  uploadedAt: string;
  uploadedBy?: string;
}

export interface IPurchaseOrder {
  id?: number;
  merchantId?: number;
  poNumber: string;
  buyingListId?: number;
  supplierId?: number;
  supplierName?: string;
  currencyCode?: string | null;
  invoiceNumber?: string | null;
  paymentStatus?: 'unpaid' | 'partial' | 'paid' | null;
  paymentDueDate?: string | null;
  status: PurchaseOrderStatus;
  automationConfigId?: number;
  orderDate: string;
  expectedDeliveryDate?: string;
  locationId?: number;
  locationName?: string;
  totalAmount: number;
  notes?: string;
  items: IPurchaseOrderItem[];
  receiveds?: IPurchaseReceived[];
  updatedAt?: string;
  createdAt?: string;
  extra?: {
    financial?: IPurchaseOrderFinancial;
    departureDate?: string;
    attachments?: IPurchaseOrderAttachment[];
    internalNotes?: string;
    [key: string]: any;
  };
}

/**
 * US-093. `updateSupplierCost` is optional and stays optional all the way to the
 * server: absent means "no preference", which the backend resolves against the
 * merchant's stored setting.
 */
export interface IFinaliseCostsPayload {
  items: Array<{ purchaseOrderItemId: number; unitPrice: number }>;
  financial?: { shipping_cost: number; adjustment_amount: number };
  invoiceNumber?: string;
  reason?: string;
  notes?: string;
  updateShopifyCost?: boolean;
  /** Mirrors Receive Products' option; only meaningful with updateShopifyCost. */
  costCalculationMethod?: CostCalculationMethod;
  updateSupplierCost?: boolean;
  markAsComplete?: boolean;
}

interface ISendEmailOptions {
  emailSubject: string;
  customMessage: string;
  attachmentFormat: 'pdf' | 'csv';
  recipients: string[];
  // AS-11593: each address gets one copy; the backend caps To + CC at 5.
  ccRecipients?: string[];
  includePricing?: boolean;
  groupByProduct?: boolean;
  // Attach the simplified one-row-per-item companion alongside the detailed PDF.
  // Ignored for csv attachments. Defaults to true server-side.
  includeSimplifiedPdf?: boolean;
  replyTo?: string[];
  companyName?: string;
  // US-069: column template to apply to the attachment. Omitted/'default' -> master template.
  templateId?: string;
}

export class PurchaseOrderModel {

  public static async createFromBuyingList(buyingListId: number, data: {
    expectedDeliveryDate?: string,
    notes?: string,
    locationId?: number,
    defaultTaxType?: { id: number, name: string, rate: number },
  } = {}): Promise<PurchaseOrderModel> {
    const response: any = await post(`/buying_lists/${buyingListId}/purchase_orders`, data);
    return new PurchaseOrderModel(response.data);
  }

  public static async getById(id: number): Promise<PurchaseOrderModel> {
    const response: any = await get(`/purchase_orders/${id}`);
    return new PurchaseOrderModel(response.data);
  }

  public static async duplicate(sourceId: number): Promise<PurchaseOrderModel> {
    const response: any = await post(`/purchase_orders/${sourceId}/duplicate`);
    return new PurchaseOrderModel(response.data);
  }

  public static async downloadItemsTemplate(): Promise<Blob> {
    return await download('/purchase_orders/download_items_template');
  }

  public static async createForImport(data: {
    supplierId?: number;
    locationId?: number;
    expectedDeliveryDate?: string;
    notes?: string;
  }): Promise<any> {
    const response = await post('/purchase_orders/create_for_import', data);
    return response;
  }
  public id?: number;
  public merchantId?: number;
  public poNumber: string;
  public buyingListId?: number;
  public supplierId?: number;
  public supplierName?: string;
  public currencyCode?: string | null;
  public invoiceNumber?: string | null;
  public paymentStatus?: 'unpaid' | 'partial' | 'paid' | null;
  public paymentDueDate?: string | null;
  public status: PurchaseOrderStatus;
  public automationConfigId?: number;
  public orderDate: string;
  public expectedDeliveryDate?: string;
  public locationId?: number;
  public locationName?: string;
  public totalAmount: number;
  public notes?: string;
  public updatedAt?: string;
  public createdAt?: string;
  public extra?: IPurchaseOrder['extra'];
  public items: IPurchaseOrderItem[];
  public receiveds: IPurchaseReceived[];
  private itemChanges: IPurchaseOrderItem[] = [];

  // ── Payment summary (Story 20) — derived from extra.financial + columns ──
  get paymentGrandTotal(): number {
    const rawGrandTotal = this.extra?.financial?.grand_total;
    if (rawGrandTotal !== null && rawGrandTotal !== undefined) {
      const grandTotal = Number(rawGrandTotal);
      if (Number.isFinite(grandTotal)) { return grandTotal; }
    }
    const totalAmount = Number(this.totalAmount);
    return Number.isFinite(totalAmount) ? totalAmount : 0;
  }

  get paidAmount(): number {
    return Number(this.extra?.financial?.paid_amount) || 0;
  }

  get outstandingAmount(): number {
    return Math.round((this.paymentGrandTotal - this.paidAmount) * 100) / 100;
  }

  // Time-based; status itself is never computed client-side (we read paymentStatus).
  get isOverdue(): boolean {
    if (!this.paymentDueDate || this.paymentStatus === 'paid') return false;
    const dueDate = this.paymentDueDate.slice(0, 10);
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return dueDate < today;
  }

  // Getter/setter for departureDate stored in extra field
  get departureDate(): string | undefined {
    return this.extra?.departureDate;
  }

  set departureDate(value: string | undefined) {
    if (!this.extra) this.extra = {};
    if (value) {
      this.extra.departureDate = value;
    } else {
      delete this.extra.departureDate;
    }
  }

  // Bill To / Ship To accessors (stored in extra field)
  private static hasAddressData(obj: any): boolean {
    if (!obj || typeof obj !== 'object') return false;
    return Object.values(obj).some((v: any) => typeof v === 'string' ? v.trim().length > 0 : Boolean(v));
  }

  get billTo(): IAddress | null {
    const obj = this.extra?.bill_to;
    return PurchaseOrderModel.hasAddressData(obj) ? obj : null;
  }

  set billTo(value: IAddress | null) {
    if (!this.extra) this.extra = {};
    this.extra.bill_to = value;
  }

  get shipTo(): IAddress | null {
    const obj = this.extra?.ship_to;
    return PurchaseOrderModel.hasAddressData(obj) ? obj : null;
  }

  set shipTo(value: IAddress | null) {
    if (!this.extra) this.extra = {};
    this.extra.ship_to = value;
  }

  // Financial data accessors
  get financial(): IPurchaseOrderFinancial {
    return this.extra?.financial ?? {};
  }

  get shippingCost(): number {
    return Number(this.extra?.financial?.shipping_cost) || 0;
  }

  set shippingCost(value: number) {
    if (!this.extra) this.extra = {};
    if (!this.extra.financial) this.extra.financial = {};
    this.extra.financial.shipping_cost = value;
  }

  get shippingTaxRate(): number {
    return Number(this.extra?.financial?.shipping_tax_rate) || 0;
  }

  set shippingTaxRate(value: number) {
    if (!this.extra) this.extra = {};
    if (!this.extra.financial) this.extra.financial = {};
    this.extra.financial.shipping_tax_rate = value;
  }

  get shippingTaxAmount(): number {
    return Number(this.extra?.financial?.shipping_tax_amount) || 0;
  }

  get discountAmount(): number {
    return Number(this.extra?.financial?.discount_amount) || 0;
  }

  set discountAmount(value: number) {
    if (!this.extra) this.extra = {};
    if (!this.extra.financial) this.extra.financial = {};
    this.extra.financial.discount_amount = value;
  }

  get discountType(): 'fixed' | 'percentage' {
    return this.extra?.financial?.discount_type || 'fixed';
  }

  set discountType(value: 'fixed' | 'percentage') {
    if (!this.extra) this.extra = {};
    if (!this.extra.financial) this.extra.financial = {};
    this.extra.financial.discount_type = value;
  }

  get discountCalculated(): number {
    return Number(this.extra?.financial?.discount_calculated) || 0;
  }

  get adjustmentAmount(): number {
    return Number(this.extra?.financial?.adjustment_amount) || 0;
  }

  set adjustmentAmount(value: number) {
    if (!this.extra) this.extra = {};
    if (!this.extra.financial) this.extra.financial = {};
    this.extra.financial.adjustment_amount = value;
  }

  get adjustmentNote(): string {
    return this.extra?.financial?.adjustment_note || '';
  }

  set adjustmentNote(value: string) {
    if (!this.extra) this.extra = {};
    if (!this.extra.financial) this.extra.financial = {};
    this.extra.financial.adjustment_note = value;
  }

  get itemTaxTotal(): number {
    return Number(this.extra?.financial?.item_tax_total) || 0;
  }

  get totalTax(): number {
    return Number(this.extra?.financial?.total_tax) || 0;
  }

  // Exchange rate accessors
  get exchangeRate(): number | null {
    return this.extra?.financial?.exchange_rate ?? null;
  }

  set exchangeRate(value: number | null) {
    if (!this.extra) this.extra = {};
    if (!this.extra.financial) this.extra.financial = {};
    this.extra.financial.exchange_rate = value;
  }

  get exchangeRateSource(): string | null {
    return this.extra?.financial?.exchange_rate_source ?? null;
  }

  get exchangeRateFetchedAt(): string | null {
    return this.extra?.financial?.exchange_rate_fetched_at ?? null;
  }

  get exchangeRateManual(): boolean {
    return this.extra?.financial?.exchange_rate_manual ?? false;
  }

  get exchangeRateLocked(): boolean {
    return this.extra?.financial?.exchange_rate_locked ?? false;
  }

  get exchangeRateLockedAt(): string | null {
    return this.extra?.financial?.exchange_rate_locked_at ?? null;
  }

  get isForeignCurrency(): boolean {
    const storeCurrency = localStorage.getItem('currency') || 'USD';
    return !!this.currencyCode && this.currencyCode !== storeCurrency;
  }

  get grandTotalBase(): number | null {
    return this.extra?.financial?.grand_total_base ?? null;
  }

  constructor(data: Partial<IPurchaseOrder>) {
    this.id = data.id;
    this.merchantId = data.merchantId;
    this.poNumber = data.poNumber || '';
    this.buyingListId = data.buyingListId;
    this.supplierId = data.supplierId;
    this.supplierName = data.supplierName || '';
    this.currencyCode = data.currencyCode || (data as any).currency_code || null;
    this.invoiceNumber = data.invoiceNumber ?? (data as any).invoice_number ?? null;
    this.paymentStatus = data.paymentStatus ?? (data as any).payment_status ?? null;
    this.paymentDueDate = data.paymentDueDate || (data as any).payment_due_date || null;
    this.status = data.status || 'draft';
    this.automationConfigId = data.automationConfigId || (data as any).automation_config_id;
    this.orderDate = data.orderDate || new Date().toISOString();
    this.expectedDeliveryDate = data.expectedDeliveryDate;
    this.locationId = data.locationId;
    this.locationName = data.locationName;
    this.totalAmount = data.totalAmount || 0;
    this.notes = data.notes;
    this.extra = data.extra;
    this.updatedAt = data.updatedAt;
    this.createdAt = data.createdAt;
    this.items = this.mappingItems(data.items || []);
    this.receiveds = data.receiveds?.reverse() || [];
  }

  public mapData(data: IPurchaseOrder): void {
    this.id = data.id;
    this.merchantId = data.merchantId;
    this.poNumber = data.poNumber;
    this.buyingListId = data.buyingListId;
    this.supplierId = data.supplierId;
    this.supplierName = data.supplierName;
    this.currencyCode = data.currencyCode || (data as any).currency_code || null;
    this.invoiceNumber = data.invoiceNumber ?? (data as any).invoice_number ?? null;
    this.paymentStatus = data.paymentStatus ?? (data as any).payment_status ?? null;
    this.paymentDueDate = data.paymentDueDate || (data as any).payment_due_date || null;
    this.status = data.status;
    this.automationConfigId = data.automationConfigId || (data as any).automation_config_id;
    this.orderDate = data.orderDate;
    this.expectedDeliveryDate = data.expectedDeliveryDate;
    this.locationId = data.locationId;
    this.locationName = data.locationName;
    this.totalAmount = data.totalAmount;
    this.notes = data.notes;
    this.extra = data.extra;
    this.updatedAt = data.updatedAt;
    this.createdAt = data.createdAt;
    this.items = this.mappingItems(data.items || []);
    this.receiveds = data.receiveds?.reverse() || [];
  }

  public mappingItems(items: IPurchaseOrderItem[]): IPurchaseOrderItem[] {
    return items.map((item) => ({
      ...item,
      variantImage: item.variantImage,
      variant: item.variant,
      sku: item.sku === 'null' ? '' : item.sku,
      supplierSku: item.supplierSku === 'null' ? '' : item.supplierSku, // Handle null supplierSku
      extra: item.extra,
    }));
  }

  public async save(): Promise<void> {
    const data = {
      poNumber: this.poNumber,
      buyingListId: this.buyingListId,
      supplierId: this.supplierId,
      status: this.status,
      orderDate: this.orderDate,
      expectedDeliveryDate: this.expectedDeliveryDate,
      locationId: this.locationId,
      currencyCode: this.currencyCode ?? null,
      notes: this.notes,
      extra: this.extra,
      items: this.items.map((item) => {
        const isNew = item._action === 'add' || !item.id;
        const mapped: any = {
          variantId: item.variantId,
          variantImage: item.variantImage,
          productName: item.productName,
          variant: item.variant,
          sku: item.sku,
          supplierSku: item.supplierSku,
          note: item.note,
          orderedQuantity: item.orderedQuantity,
          receivedQuantity: item.receivedQuantity || 0,
          unitPrice: item.unitPrice,
          totalPrice: item.orderedQuantity * item.unitPrice,
          _action: isNew ? 'add' : (item._action || 'update'),
          extra: item.extra,
        };
        if (!isNew) {
          mapped.id = item.id;
        }
        return mapped;
      }),
    };

    if (this.id) {
      const response: any = await put(`/purchase_orders/${this.id}`, data);
      if (response?.data) {
        this.mapData(response.data);
      }
    } else {
      const response: any = await post('/purchase_orders', data);
      this.id = response.data.id;
      this.poNumber = response.data.poNumber || this.poNumber;
    }
  }

  public async updateStatus(newStatus: PurchaseOrderStatus): Promise<void> {
    if (!this.id) { throw new Error('Purchase order ID is required'); }

    // Defensive check: cannot change status of terminal POs (completed/canceled),
    // except the US-088 re-open of a completed PO back to 'received'.
    if (this.isTerminalStatus() && !(newStatus === 'received' && this.canReopen())) {
      throw new Error(`Cannot update status: Purchase order is in terminal status "${this.status}" and cannot be modified.`);
    }

    // Defensive check: validate specific transitions
    if (newStatus === 'completed' && !this.canMarkAsCompleted()) {
      throw new Error(`Cannot mark as complete: Purchase order status "${this.status}" does not allow marking as complete.`);
    }

    if (newStatus === 'canceled' && !this.canCancel()) {
      throw new Error(`Cannot cancel: Purchase order status "${this.status}" does not allow cancellation. Only draft or confirmed POs can be canceled.`);
    }

    let response: any = await put(`/purchase_orders/${this.id}/status`, {
      status: newStatus,
    });
    
    if (typeof response === 'string') {
      response = JSON.parse(response);
    }

    // Update local status
    this.mapData(response.data);
  }

  public async delete(): Promise<void> {
    if (this.id) {
      await del(`/purchase_orders/${this.id}`);
    }
  }

  public async fetchItems(): Promise<void> {
    if (!this.id) { return; }
    const response: any = await get(`/purchase_orders/${this.id}/items`);
    this.items = response.data.items || [];
  }

  async receiveItems(
    itemUpdates: { id: number, receivedQuantity: number }[],
    notes?: string,
    deliveryNoteNumber?: string,
    updateShopifyInventory: boolean = true,
    costData?: IPurchaseOrderCostData,
    markAsComplete: boolean = false,
    idempotencyKey?: string,
    // US-092. Independent of costData.updateShopifyCosts: this decides whether
    // the entered clean cost becomes the variant's SupplierProduct.cost.
    //
    // OPTIONAL on purpose — omitting it is meaningful. The backend then resolves
    // the merchant's stored preference, which is what a caller that cannot read
    // that preference (a failed fetch, or the collection wrapper) must do rather
    // than assert a default that would override an opt-out.
    updateSupplierCost?: boolean
  ): Promise<any> {
    if (!this.id) return;

    // Defensive check: fail fast for invalid status
    if (!this.canReceiveItems()) {
      throw new Error(`Cannot receive items: Purchase order status "${this.status}" does not allow receiving. Only confirmed or partial_received POs can receive items.`);
    }

    // No separate markAsComplete check: Receive & Complete is allowed wherever
    // receiving is (including `confirmed`), because the backend receives first
    // and completes after. canMarkAsCompleted() is the direct status change only.

    // First validate the updates
    for (const update of itemUpdates) {
      const item = this.items.find((i) => Number(i.id) === Number(update.id));
      if (!item) {
        throw new Error(`Item ${update.id} not found in purchase order`);
      }

      // Allow over-receiving - removed validation that prevented receiving more than ordered quantity
    }

    // Prepare request payload
    const payload: any = {
      items: itemUpdates,
      notes,
      deliveryNoteNumber,
      updateShopifyInventory,
      markAsComplete,  // Include mark as complete flag
      ...(updateSupplierCost === undefined ? {} : { updateSupplierCost }),
      // One key per receive intent — lets the backend reject a duplicate
      // re-submission (e.g. retry after a lost response) instead of
      // double-adjusting Shopify inventory.
      idempotencyKey: idempotencyKey || undefined
    };

    // Add cost tracking data if provided (type-safe access)
    if (costData?.enabled) {
      payload.updateShopifyCost = costData.updateShopifyCosts;
      payload.costCalculationMethod = costData.method;
      if (costData.items && costData.items.length > 0) {
        payload.costItems = costData.items;
      }
    }

    const response: any = await post(`/purchase_orders/${this.id}/receive`, payload);

    // Update local items with received quantities.
    // `data` can be null when the backend degraded a post-commit failure or
    // deduplicated the submission — the caller re-fetches in that case.
    if (response.data) {
      this.mapData(response.data);
    }

    // Return response for status checking (keep `data` shape for callers,
    // but surface duplicate/postCommitWarning flags too)
    return {
      ...(response.data || {}),
      inventoryUpdateStatus: response.inventoryUpdateStatus,
      costUpdateStatus: response.costUpdateStatus,
      receiveRecordId: response.receiveRecordId,
      duplicate: response.duplicate || false,
      postCommitWarning: response.postCommitWarning
    };
  }

  /**
   * Retry Shopify inventory sync for a failed receive record
   * @param receiveRecordId - The ID of the purchase received record to retry
   * @returns Response data including updated inventoryUpdateStatus
   */
  async retryShopifyInventorySync(receiveRecordId: number): Promise<any> {
    if (!this.id) {
      throw new Error('Purchase order ID is required');
    }

    if (!receiveRecordId) {
      throw new Error('Receive record ID is required');
    }

    const response: any = await post(
      `/purchase_orders/${this.id}/received/${receiveRecordId}/retry-shopify-sync`,
      {}
    );

    return response.data;
  }

  /**
   * Retry Shopify cost sync for a failed receive record
   * @param receiveRecordId - The ID of the purchase received record to retry cost sync
   * @returns Response data including updated cost sync status
   */
  async retryCostSync(receiveRecordId: number): Promise<any> {
    if (!this.id) {
      throw new Error('Purchase order ID is required');
    }

    if (!receiveRecordId) {
      throw new Error('Receive record ID is required');
    }

    const response: any = await post(
      `/purchase_orders/${this.id}/received/${receiveRecordId}/retry-cost-sync`,
      {}
    );

    return response.data;
  }

  /**
   * US-093 — finalise this PO's costs after the goods were received.
   *
   * The payload is built by `poCostFinalization.buildFinalizationPayload`, which
   * is where the "clean supplier cost, never landed" rule lives. It is passed
   * through untouched on purpose: adding a default for `updateSupplierCost` here
   * would override the merchant's stored preference.
   */
  async finaliseCosts(payload: IFinaliseCostsPayload): Promise<any> {
    if (!this.id) {
      throw new Error('Purchase order ID is required');
    }

    const response: any = await post(`/purchase_orders/${this.id}/costs`, payload);

    if (response.data?.purchaseOrder) {
      this.mapData(response.data.purchaseOrder);
    }

    return response.data;
  }

  public async sendToSupplier(options: ISendEmailOptions): Promise<void> {
    if (!this.id) { throw new Error('Purchase order not initialized'); }

    // Validate required fields
    if (!options.recipients || options.recipients.length === 0) {
      throw new Error('At least one recipient is required');
    }

    if (!options.emailSubject || !options.attachmentFormat) {
      throw new Error('Email subject and attachment format are required');
    }

    try {
      await post(`/purchase_orders/${this.id}/send`, {
        emailSubject: options.emailSubject,
        customMessage: options.customMessage,
        attachmentFormat: options.attachmentFormat,
        recipients: options.recipients,
        ccRecipients: options.ccRecipients && options.ccRecipients.length > 0 ? options.ccRecipients : undefined,
        includePricing: options.includePricing !== false, // Default to true
        groupByProduct: options.groupByProduct || false,
        includeSimplifiedPdf: options.includeSimplifiedPdf !== false, // Default to true
        replyTo: options.replyTo || undefined,
        companyName: options.companyName || undefined,
        templateId: options.templateId || undefined, // US-069: column template for the attachment
        changeStatusToSent: false, // Do not change status - email is tracked via history only
      });
    } catch (err: any) {
      const rateLimitErr = parsePoEmailRateLimitError(err);
      if (rateLimitErr) throw rateLimitErr;
      throw err;
    }
  }

  public async removeItem(itemId: number): Promise<void> {
    if (!this.id) { throw new Error('Purchase order ID is required'); }

    // Check if item exists in this PO
    const itemExists = this.items.some((item) => item.id === itemId);
    if (!itemExists) {
      throw new Error('Item not found in this purchase order');
    }

    // Call API to remove the item
    const response: any = await del(`/purchase_orders/${this.id}/items/${itemId}`);

    // Update local items list and total amount
    // Note: del() already returns res.data, so response = { message: '...', data: { totalAmount: ... } }
    this.items = this.items.filter((item) => item.id !== itemId);
    this.totalAmount = response.data?.totalAmount || 0;
  }

  public async downloadReceiveTemplate(): Promise<Blob> {
    if (!this.id) { throw new Error('Purchase order ID is required'); }

    // Use the download service function for file downloads
    return await download(`/purchase_orders/${this.id}/download_receive_template`);
  }

  async importReceiveFromCSV(
    csvFile: File,
    deliveryNoteNumber?: string,
    receiveNotes?: string,
    updateShopifyInventory: boolean = true,
    costData?: IPurchaseOrderCostData,
    idempotencyKey?: string,
    // US-092 — the CSV `Unit Cost` column is the supplier's clean unit price,
    // same as the dialog input, so it feeds SupplierProduct.cost the same way.
    // Optional for the same reason as receiveItems above.
    updateSupplierCost?: boolean
  ): Promise<any> {
    if (!this.id) throw new Error('Purchase order ID is required');

    // Defensive check: fail fast for invalid status
    if (!this.canReceiveItems()) {
      throw new Error(`Cannot import receive: Purchase order status "${this.status}" does not allow receiving. Only confirmed or partial_received POs can receive items.`);
    }

    // Create FormData for file upload
    const formData = new FormData();
    formData.append('csvFile', csvFile);

    if (deliveryNoteNumber) {
      formData.append('deliveryNoteNumber', deliveryNoteNumber);
    }

    if (receiveNotes) {
      formData.append('receiveNotes', receiveNotes);
    }

    formData.append('updateShopifyInventory', updateShopifyInventory.toString());
    // Absent, not 'true', when unknown — the backend treats a missing multipart
    // field as "no preference expressed" and reads the merchant's setting.
    if (updateSupplierCost !== undefined) {
      formData.append('updateSupplierCost', updateSupplierCost.toString());
    }

    // One key per receive intent — lets the backend reject a duplicate
    // re-submission instead of double-adjusting Shopify inventory.
    if (idempotencyKey) {
      formData.append('idempotencyKey', idempotencyKey);
    }

    // Add cost tracking data if provided and enabled (match receiveItems behavior)
    if (costData?.enabled) {
      formData.append('updateShopifyCost', costData.updateShopifyCosts.toString());
      formData.append('costCalculationMethod', costData.method);

      if (costData.items && costData.items.length > 0) {
        formData.append('costItems', JSON.stringify(costData.items));
      }
    }
    
    // Make the API call using post with isForm=true for multipart/form-data
    const response: any = await post(`/purchase_orders/${this.id}/import_receive`, formData, true);

    // Update local data with the response
    if (response.data) {
      this.mapData(response.data);
    }

    return response;
  }

  public calculateTotalAmount(): number {
    const total = this.items.reduce((sum, item) => {
      return sum + parseFloat((item.totalPrice || 0).toString());
    }, 0);
    this.totalAmount = total;
    return total;
  }

  public getStatusText(): string {
    const statusMap: Record<IPurchaseOrder['status'], string> = {
      draft: 'Draft',
      pending_approval: 'Pending Approval',
      confirmed: 'Confirmed',
      partial_received: 'Partially Received',
      received: 'Fully Received',
      completed: 'Completed',
      canceled: 'Canceled',
    };

    return statusMap[this.status] || this.status;
  }

  public getStatusColor(): string {
    const colorMap: Record<IPurchaseOrder['status'], string> = {
      draft: 'grey',
      pending_approval: 'orange',
      confirmed: 'amber',
      partial_received: 'light-green',
      received: 'green',
      completed: 'teal',
      canceled: 'red',
    };

    return colorMap[this.status] || 'grey';
  }

  public canEdit(): boolean {
    return this.status === 'draft';
  }

  public canEditMetadata(): boolean {
    return !this.isTerminalStatus();
  }

  public canCancel(): boolean {
    // Cannot cancel once receiving has started (partial_received, received, or completed)
    return ['draft', 'confirmed'].includes(this.status);
  }

  public canReceiveItems(): boolean {
    // Can only receive items before fully received (use returnItems for received POs)
    return ['confirmed', 'partial_received'].includes(this.status);
  }

  public canSendToSupplier(): boolean {
    return this.status === 'draft';
  }

  // A direct status change to completed (AS-11374). Matches the backend's
  // allowedTransitions in purchase_order_controller.js, where only
  // partial_received and received may move to completed. partial_received with
  // lines outstanding is the write-off case and is allowed on purpose.
  public canMarkAsCompleted(): boolean {
    return ['partial_received', 'received'].includes(this.status);
  }

  public isTerminalStatus(): boolean {
    return ['completed', 'canceled'].includes(this.status);
  }

  // US-088: a completed PO is locked, not a dead end. Re-opening moves it back
  // to 'received' — the only transition the backend allows out of 'completed'
  // (see allowedTransitions in purchase_order_controller.js). Canceled stays
  // genuinely terminal.
  public canReopen(): boolean {
    return this.status === 'completed';
  }

  public canReturnItems(): boolean {
    // Can return items from partial_received, received, or completed POs
    return ['partial_received', 'received', 'completed'].includes(this.status);
  }

  /**
   * Return items to supplier
   * @param items - Array of items to return with itemId, returnQuantity, and reason
   * @param notes - Optional notes about the return
   * @param updateShopifyInventory - Whether to update Shopify inventory (reduce quantities)
   */
  async returnItems(
    items: { itemId: number; returnQuantity: number; reason: string }[],
    notes?: string,
    updateShopifyInventory: boolean = true
  ): Promise<any> {
    if (!this.id) {
      throw new Error('Cannot return items: Purchase order ID is required');
    }

    if (!this.canReturnItems()) {
      throw new Error(`Cannot return items: Purchase order status "${this.status}" does not allow returns. Only partial_received, received, or completed POs can have items returned.`);
    }

    if (!items || !Array.isArray(items) || items.length === 0) {
      throw new Error('Cannot return items: At least one item must be provided');
    }

    // Get valid item IDs from this purchase order
    const validItemIds = new Set(this.items?.map(i => i.id).filter((id): id is number => id !== undefined) || []);

    for (const item of items) {
      if (!item.itemId || item.itemId <= 0) {
        throw new Error(`Cannot return items: Invalid itemId "${item.itemId}". Each item must have a valid itemId.`);
      }
      if (!item.returnQuantity || item.returnQuantity <= 0) {
        throw new Error(`Cannot return items: Invalid returnQuantity "${item.returnQuantity}" for itemId ${item.itemId}. Return quantity must be greater than 0.`);
      }
      // Validate itemId belongs to this purchase order (only if items are loaded)
      if (validItemIds.size > 0 && !validItemIds.has(item.itemId)) {
        throw new Error(`Cannot return items: itemId ${item.itemId} does not belong to this purchase order.`);
      }
    }

    const payload = {
      items,
      notes,
      updateShopifyInventory
    };

    const response: any = await post(`/purchase_orders/${this.id}/return`, payload);

    this.mapData(response.data);

    return response.data;
  }

  /**
   * Adjust ordered quantity on lines of a processed PO.
   * Server computes delta from current orderedQuantity; client sends target qty.
   * Eligible statuses: confirmed, partial_received, received.
   */
  async adjustQuantity(
    items: { purchaseOrderItemId: number; newOrderedQuantity: number }[],
    reason: string,
    notes?: string,
  ): Promise<any> {
    if (!this.id) {
      throw new Error('Cannot adjust quantity: Purchase order ID is required');
    }

    const eligibleStatuses = ['confirmed', 'partial_received', 'received'];
    if (!eligibleStatuses.includes(this.status as string)) {
      throw new Error(`Cannot adjust quantity: Purchase order status "${this.status}" does not allow adjustments. Only confirmed, partial_received, or received POs can be adjusted.`);
    }

    if (!Array.isArray(items) || items.length === 0) {
      throw new Error('Cannot adjust quantity: At least one item must be provided');
    }
    if (!reason || reason.trim() === '') {
      throw new Error('Cannot adjust quantity: Reason is required');
    }

    // Sequelize BIGINTs can arrive as numeric strings in JSON payloads,
    // so normalize before validating / posting (Number.isFinite("123") is false).
    const validItemIds = new Set(
      (this.items || [])
        .map((i) => Number(i.id))
        .filter((id) => Number.isFinite(id) && id > 0),
    );

    const normalized: { purchaseOrderItemId: number; newOrderedQuantity: number }[] = [];
    for (const entry of items) {
      const lineId = Number(entry.purchaseOrderItemId);
      const newQty = Number(entry.newOrderedQuantity);
      if (!Number.isFinite(lineId) || lineId <= 0) {
        throw new Error(`Cannot adjust quantity: Invalid purchaseOrderItemId "${entry.purchaseOrderItemId}"`);
      }
      if (!Number.isInteger(newQty) || newQty < 0) {
        throw new Error(`Cannot adjust quantity: Invalid newOrderedQuantity "${entry.newOrderedQuantity}" for line ${lineId}`);
      }
      if (validItemIds.size > 0 && !validItemIds.has(lineId)) {
        throw new Error(`Cannot adjust quantity: line ${lineId} does not belong to this purchase order`);
      }
      normalized.push({ purchaseOrderItemId: lineId, newOrderedQuantity: newQty });
    }

    const response: any = await post(`/purchase_orders/${this.id}/adjust-quantity`, {
      items: normalized,
      reason,
      notes: notes || null,
    });

    if (response?.data?.purchaseOrder) {
      this.mapData(response.data.purchaseOrder);
    }

    return response.data;
  }

  /**
   * AS-10746: apply, change or clear the tax on every line of this PO.
   *
   * A dedicated endpoint rather than the general `update`: tax stays editable
   * through `received` because it does not enter inventory valuation, while
   * quantity and unit price — which do — stay locked past draft.
   */
  public async applyTax(payload: ITaxApplyPayload): Promise<any> {
    if (!this.id) {
      throw new Error('Cannot apply tax: purchase order has no id');
    }
    const response: any = await patch(`/purchase_orders/${this.id}/items/tax`, payload);

    if (response?.data?.purchaseOrder) {
      this.mapData(response.data.purchaseOrder);
    }

    return response?.data;
  }

  /**
   * Fetch history for this purchase order (status changes, emails)
   */
  public async getHistory(): Promise<IPurchaseOrderHistoryItem[]> {
    if (!this.id) { return []; }

    try {
      const response: any = await get(`/purchase_orders/${this.id}/history`);
      const rawHistory = response.history;

      if (!Array.isArray(rawHistory)) {
        return [];
      }

      // Validate and normalize each history item
      // US-093 ('cost_correction') and AS-10746 ('tax_change') must BOTH be
      // listed here, or the audit row the backend writes is silently dropped
      // before it reaches the timeline — which is exactly what history is for.
      const validTypes: PurchaseOrderHistoryType[] = [
        'status_change', 'email_sent', 'receive', 'return', 'cost_correction', 'tax_change',
      ];

      return rawHistory
        .filter((item: any) => item && typeof item === 'object' && validTypes.includes(item.type))
        .map((item: any): IPurchaseOrderHistoryItem => ({
          type: item.type as PurchaseOrderHistoryType,
          date: item.createdAt || item.date || null,
          data: item.data || undefined,
          performedBy: item.performedBy || undefined,
          source: 'audit'
        }));
    } catch (error) {
      console.error('Error fetching PO history:', error);
      return [];
    }
  }

  public toJSON(): IPurchaseOrder {
    return {
      id: this.id,
      merchantId: this.merchantId,
      poNumber: this.poNumber,
      buyingListId: this.buyingListId,
      supplierId: this.supplierId,
      supplierName: this.supplierName,
      currencyCode: this.currencyCode,
      status: this.status,
      automationConfigId: this.automationConfigId,
      orderDate: this.orderDate,
      expectedDeliveryDate: this.expectedDeliveryDate,
      locationId: this.locationId,
      locationName: this.locationName,
      totalAmount: this.totalAmount,
      notes: this.notes,
      invoiceNumber: this.invoiceNumber,
      paymentStatus: this.paymentStatus,
      paymentDueDate: this.paymentDueDate,
      items: this.items,
      receiveds: this.receiveds,
      extra: this.extra,
      updatedAt: this.updatedAt,
      createdAt: this.createdAt,
    };
  }

  public async importItems(file: File, columnMapping?: any): Promise<any> {
    if (!this.id) { throw new Error('Purchase order ID is required'); }
    const formData = new FormData();
    formData.append('csvFile', file);
    if (columnMapping) {
      formData.append('columnMapping', JSON.stringify(columnMapping));
    }
    return await post(`/purchase_orders/${this.id}/import_items`, formData, true);
  }

  // Attachment management methods
  public getAttachments(): IPurchaseOrderAttachment[] {
    if (!this.extra || !this.extra.attachments) {
      return [];
    }
    return this.extra.attachments;
  }

  public addAttachments(newAttachments: IPurchaseOrderAttachment[]): void {
    if (!this.extra) {
      this.extra = {};
    }
    if (!this.extra.attachments) {
      this.extra.attachments = [];
    }
    this.extra.attachments.push(...newAttachments);
  }

  public removeAttachment(index: number): void {
    if (!this.extra || !this.extra.attachments) {
      return;
    }
    this.extra.attachments.splice(index, 1);
  }

  public async update(data: Partial<IPurchaseOrder>): Promise<void> {
    if (!this.id) {
      throw new Error('Purchase order ID is required for updates');
    }

    // Update local properties
    Object.assign(this, data);

    // Send update to server
    const updateData = {
      supplierId: this.supplierId,
      expectedDeliveryDate: this.expectedDeliveryDate,
      locationId: this.locationId,
      notes: this.notes,
      extra: this.extra,
    };

    const response: any = await put(`/purchase_orders/${this.id}`, updateData);

    // Update local data with server response
    if (response.data) {
      this.mapData(response.data);
    }
  }
}